<script lang="ts" setup>
import type { BrokerUpdatesOrderDto, TrucksByTravelOrderId } from "@/api/trips/dto/trip.dto";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { Trip } from "@/views/trips/edit/trip";

const location = useRoute();
const router = useRouter();

const props = defineProps<{
  trip: Trip;
}>();

const emits = defineEmits<{
  (e: "change"): void;
}>();

const uniqueOrders = ref<BrokerUpdatesOrderDto[]>([]);
const trucksByTravelOrder = ref<TrucksByTravelOrderId>({});

async function createTrucksByTravelOrder() {
  const entries = await Promise.all(
    props.trip.routes.flatMap(async (route) => {
      return route.travelOrders
        .filter(({ id }) => id)
        .map(({ id }) => {
          return [id, route.truck];
        });
    }),
  );

  trucksByTravelOrder.value = Object.fromEntries(entries.flat());
}

function createUniqueOrders() {
  if (!props.trip?.routes) {
    uniqueOrders.value = [];
    return;
  }

  const ordersMap: Record<string, BrokerUpdatesOrderDto> = {};

  props.trip.routes.forEach((route) => {
    route.travelOrders
      .filter(({ id }) => id)
      .forEach((travelOrder) => {
        const orderId = travelOrder.order.id || "";
        const truck = trucksByTravelOrder.value[travelOrder.id!];

        if (!ordersMap[orderId]) {
          ordersMap[orderId] = {
            ...travelOrder.order,
            travelOrders: [],
          };
        }

        ordersMap[orderId].travelOrders.push({
          ...travelOrder,
          truck,
          routeId: route.id,
        });
      });
  });

  uniqueOrders.value = Object.values(ordersMap);
}

watch(
  () => props.trip,
  async (value) => {
    await createTrucksByTravelOrder();
    createUniqueOrders();

    if (!location.params.orderId && value) {
      await router.push({
        name: "trip-broker-updates-order",
        params: { id: props.trip.id, orderId: uniqueOrders.value[0].id },
      });
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="my-4">
    <v-btn
      v-for="order in uniqueOrders"
      :key="order.id"
      :to="{ name: 'trip-broker-updates-order', params: { id: trip.id, orderId: order.id } }"
      active-color="primary"
      class="text-uppercase"
      variant="flat"
    >
      Order #{{ order.number }}
    </v-btn>

    <router-view v-slot="{ Component }">
      <component :is="Component" :orders="uniqueOrders" :routes="trip.routes" @update="emits('change')" />
    </router-view>
  </div>
</template>

<style lang="scss" scoped></style>
